import type { Animation, Overlay } from '../models/AnimationTypes';

/**
 * Parsed fade animation info extracted from legacy color_overlay arrays.
 */
export interface ParsedFadeInfo {
  enter?: { type: 'dip' | 'crossfade'; duration: number };
  exit?: { type: 'dip' | 'crossfade'; duration: number };
}

/**
 * UUID pattern used in legacy crossfade easing strings.
 * Format: "uuid-cross-fade-start.duration" or "uuid-cross-fade-end.duration"
 */
const UUID_CROSSFADE_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-cross-fade/i;

/**
 * Parses legacy color_overlay arrays to detect dip or crossfade animations.
 *
 * Detection heuristics (from FadeProducer):
 * - `quadratic-in` easing with rgba(0,0,0,*) values → dip enter
 * - `quadratic-out*` easing with rgba(0,0,0,*) values → dip exit
 * - `ease-in` easing with rgba(0,0,0,*) values → crossfade enter
 * - `ease-out*` easing with rgba(0,0,0,*) values → crossfade exit
 * - Easing containing UUID + "cross-fade" → legacy crossfade on main video (strip)
 *
 * @returns Parsed fade info or null if no legacy fade pattern detected.
 */
export function parseFromOverlays(
  colorOverlay: Overlay[] | string | null | undefined,
): ParsedFadeInfo | null {
  if (!colorOverlay || typeof colorOverlay === 'string') return null;
  if (!Array.isArray(colorOverlay) || colorOverlay.length === 0) return null;

  // Filter to only black-value overlays (rgba(0,0,0,*))
  const fadeOverlays = colorOverlay.filter(
    o => typeof o.value === 'string' && o.value.startsWith('rgba(0, 0, 0,'),
  );
  if (fadeOverlays.length === 0) return null;

  const enter = parseEnterFade(fadeOverlays);
  const exit = parseExitFade(fadeOverlays);

  if (!enter && !exit) return null;

  return {
    ...(enter ? { enter } : {}),
    ...(exit ? { exit } : {}),
  };
}

/**
 * Detects whether a color_overlay on an original video track element
 * contains legacy crossfade entries (with UUID-prefixed easing strings)
 * that should be stripped during migration.
 */
export function hasLegacyCrossfadeOverlays(
  colorOverlay: Overlay[] | string | null | undefined,
): boolean {
  if (!colorOverlay || typeof colorOverlay === 'string') return false;
  if (!Array.isArray(colorOverlay)) return false;
  return colorOverlay.some(
    o => typeof o.easing === 'string' && UUID_CROSSFADE_PATTERN.test(o.easing),
  );
}

/**
 * Strips legacy crossfade overlay entries (UUID-prefixed) from a color_overlay
 * array. Returns null if all entries are stripped.
 */
export function stripLegacyCrossfadeOverlays(
  colorOverlay: Overlay[] | string | null | undefined,
): Overlay[] | null {
  if (!colorOverlay || typeof colorOverlay === 'string') return null;
  if (!Array.isArray(colorOverlay)) return null;
  const filtered = colorOverlay.filter(
    o =>
      !(typeof o.easing === 'string' && UUID_CROSSFADE_PATTERN.test(o.easing)),
  );
  return filtered.length > 0 ? filtered : null;
}

/**
 * Converts parsed legacy fade info into native Animation objects
 * that can be added to an element's animations array.
 */
export function toNativeAnimations(parsed: ParsedFadeInfo): Animation[] {
  const animations: Animation[] = [];
  if (parsed.enter) {
    animations.push({
      type: parsed.enter.type,
      time: 'start',
      duration: parsed.enter.duration,
    });
  }
  if (parsed.exit) {
    animations.push({
      type: parsed.exit.type,
      time: 'end',
      duration: parsed.exit.duration,
    });
  }
  return animations;
}

// ─── Internal helpers ────────────────────────────────────────────────────────

function parseEnterFade(
  overlays: Overlay[],
): { type: 'dip' | 'crossfade'; duration: number } | undefined {
  // Look for quadratic-in (dip) or ease-in (crossfade) at time 0
  const startOverlay = overlays.find(
    o =>
      (o.time === 0 || o.time === 'start') &&
      (o.easing === 'quadratic-in' || o.easing === 'ease-in'),
  );
  if (!startOverlay) return undefined;

  const isDip = startOverlay.easing === 'quadratic-in';

  // Find the matching end-of-fade overlay (where value transitions to transparent)
  const endOfFade = overlays.find(
    o =>
      o.time !== 0 &&
      o.time !== 'start' &&
      o.time !== 'end' &&
      o.easing === startOverlay.easing &&
      o.value === 'rgba(0, 0, 0, 0)',
  );

  if (endOfFade && typeof endOfFade.time === 'number') {
    // For crossfade, the overlay duration is half the perceived duration
    // (legacy FadeProducer used duration/2 for the overlay)
    const duration = isDip ? endOfFade.time : endOfFade.time * 2;
    return { type: isDip ? 'dip' : 'crossfade', duration };
  }

  // Fallback: try to extract duration from any non-start/end overlay time
  const anyMiddle = overlays.find(
    o => typeof o.time === 'number' && o.time !== 0,
  );
  if (anyMiddle && typeof anyMiddle.time === 'number') {
    const duration = isDip ? anyMiddle.time : anyMiddle.time * 2;
    return { type: isDip ? 'dip' : 'crossfade', duration };
  }

  return { type: isDip ? 'dip' : 'crossfade', duration: 1 };
}

function parseExitFade(
  overlays: Overlay[],
): { type: 'dip' | 'crossfade'; duration: number } | undefined {
  // Look for quadratic-out* (dip) or ease-out* (crossfade) at time 'end'
  const endOverlay = overlays.find(
    o =>
      o.time === 'end' &&
      typeof o.easing === 'string' &&
      (o.easing.includes('quadratic-out') || o.easing.includes('ease-out')),
  );
  if (!endOverlay) return undefined;

  const isDip = endOverlay.easing.includes('quadratic-out');

  // Extract duration from the easing string suffix (e.g., "quadratic-out1" → 1, "ease-out0.5" → 0.5)
  const easingBase = isDip ? 'quadratic-out' : 'ease-out';
  const suffix = endOverlay.easing.replace(easingBase, '');
  const parsedDuration = suffix ? parseFloat(suffix) : NaN;

  if (!isNaN(parsedDuration) && parsedDuration > 0) {
    return { type: isDip ? 'dip' : 'crossfade', duration: parsedDuration };
  }

  // Fallback: look for the penultimate overlay to calculate duration from time difference
  const penultimate = overlays.find(
    o =>
      typeof o.time === 'number' &&
      typeof o.easing === 'string' &&
      (o.easing.includes('quadratic-out') || o.easing.includes('ease-out')) &&
      o.value === 'rgba(0, 0, 0, 0)',
  );
  if (penultimate && typeof penultimate.time === 'number') {
    // We can't compute exact duration without element duration, use 1 as default
    return { type: isDip ? 'dip' : 'crossfade', duration: 1 };
  }

  return { type: isDip ? 'dip' : 'crossfade', duration: 1 };
}
